/**
 * Keeps floating overlays correct across the iPad on-screen keyboard
 * animation.
 *
 * The combobox panel clamps its height against `window.visualViewport` every
 * time floating-ui re-positions it, and floating-ui *does* re-position on
 * visual-viewport changes — `visualViewport` is one of the overflow ancestors
 * `autoUpdate` binds `scroll`/`resize` to. So the signal is not missing.
 *
 * The problem is which signal lands last. iOS/iPadOS fires `resize` WHILE the
 * keyboard animates, repeatedly, with intermediate heights, and is not
 * guaranteed to fire again once the keyboard settles. When the final event of a
 * burst carries a mid-animation height, every open overlay is left sized for a
 * viewport that stops existing a frame later — the panel extends behind the
 * keyboard and nothing triggers another pass to correct it. Whether the last
 * event happens to be the settled one is a race, which is why the symptom is
 * intermittent rather than constant.
 *
 * So after a real resize we keep re-broadcasting `resize` on the visual
 * viewport until its height has held still for a few polls, guaranteeing that
 * the last positioning pass any listener sees is the settled geometry. Events
 * are dispatched on `visualViewport` rather than `window` deliberately: it is
 * the narrower channel, and floating-ui is currently its only subscriber.
 */

/** Poll cadence while the viewport is in motion. Comfortably inside the ~250ms
 *  iOS keyboard animation without busy-waiting. */
const POLL_MS = 80

/** Consecutive equal heights before the viewport counts as settled. Three at
 *  80ms means ~240ms of stillness, which clears the keyboard animation and the
 *  predictive-text bar appearing a beat later. */
const STABLE_POLLS = 3

/** Hard stop, so a viewport that never stabilises (continuous scroll-pan,
 *  pinch-zoom) cannot leave the poll running indefinitely. */
const TIMEOUT_MS = 1200

export const installVisualViewportSettle = (
  viewport: VisualViewport | null | undefined = typeof window !== 'undefined'
    ? window.visualViewport
    : null
): (() => void) => {
  if (!viewport) return () => {}

  let pollId: ReturnType<typeof setInterval> | undefined
  let deadline = 0
  let lastHeight = viewport.height
  let stablePolls = 0
  // Set while we dispatch, so our own event never restarts the poll.
  let broadcasting = false

  const stopPolling = () => {
    if (pollId !== undefined) {
      clearInterval(pollId)
      pollId = undefined
    }
  }

  const broadcast = () => {
    broadcasting = true
    try {
      viewport.dispatchEvent(new Event('resize'))
    } finally {
      broadcasting = false
    }
  }

  const poll = () => {
    if (viewport.height === lastHeight) {
      stablePolls += 1
    } else {
      lastHeight = viewport.height
      stablePolls = 0
    }

    const settled = stablePolls >= STABLE_POLLS
    const expired = Date.now() >= deadline
    if (settled || expired) stopPolling()

    // Broadcast on every tick: mid-animation passes keep the panel tracking the
    // shrinking viewport, and the pass after `stopPolling` is the one that
    // leaves it sized for the settled geometry.
    broadcast()
  }

  const handleResize = () => {
    if (broadcasting) return
    lastHeight = viewport.height
    stablePolls = 0
    deadline = Date.now() + TIMEOUT_MS
    if (pollId === undefined) pollId = setInterval(poll, POLL_MS)
  }

  viewport.addEventListener('resize', handleResize)

  return () => {
    stopPolling()
    viewport.removeEventListener('resize', handleResize)
  }
}
